"use client";

import { MatterContactConnectionRichCard } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { type Contact, type MatterContactConnection } from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import { toFormString } from "@/lib/utils/formFieldCoercion";
import type { MatterContactHeroTone } from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import { getlabel } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  normalizeContactFormEmails,
  normalizeContactFormPhones,
} from "./contactFormCoercion";
import type { CreateContactFormValues } from "./contactFormSchema";

export function ContactFormLivePreview({
  matterContactConnection,
  matterId,
  slotLabel = "Contact",
  tone = "gray",
  onRemoveContact,
}: {
  matterContactConnection?: MatterContactConnection;
  matterId?: string;
  slotLabel?: string;
  tone?: MatterContactHeroTone;
  onRemoveContact?: () => void;
}) {
  const { control } = useFormContext<CreateContactFormValues>();
  const watched = useWatch({ control });
  const normalizedPhones = useMemo(
    () => normalizeContactFormPhones(watched.phones),
    [watched.phones],
  );
  const normalizedEmails = useMemo(
    () => normalizeContactFormEmails(watched.emails),
    [watched.emails],
  );
  const connectionContactId = (
    matterContactConnection as Partial<MatterContactConnection> | undefined
  )?.contactId;

  const previewContact = useMemo((): Contact => {
    const contact = matterContactConnection?.contact;
    const type = watched.type ?? contact?.type ?? "individual";
    const draftName =
      formatContactsName({
        type,
        individualFirstName: watched.individualFirstName ?? null,
        individualLastName: watched.individualLastName ?? null,
        individualNickname: watched.individualNickname ?? null,
        organizationName: watched.organizationName ?? null,
        groupName: watched.groupName ?? null,
      })?.trim() ||
      toFormString(watched.organizationName).trim() ||
      toFormString(watched.groupName).trim() ||
      "";

    return {
      addresses: Array.isArray(watched.addresses)
        ? (watched.addresses as Contact["addresses"])
        : (contact?.addresses ?? []),
      connectionId: matterContactConnection?.id,
      description: matterContactConnection?.description ?? "",
      emails: normalizedEmails as Contact["emails"],
      faxes: contact?.faxes ?? [],
      id: connectionContactId?.trim() ?? contact?.id ?? "",
      type,
      contactName: draftName || "",
      phones: normalizedPhones as Contact["phones"],
      tagConnections: contact?.tagConnections ?? [],
      individualSubTypes: [
        ...(watched.individualSubTypes ?? contact?.individualSubTypes ?? []),
      ],
      organizationSubTypes: [
        ...(watched.organizationSubTypes ??
          contact?.organizationSubTypes ??
          []),
      ],
      label: getlabel(matterContactConnection ?? {}).trim() || slotLabel,
    } as unknown as Contact;
  }, [
    matterContactConnection,
    connectionContactId,
    normalizedEmails,
    normalizedPhones,
    slotLabel,
    watched,
  ]);

  return (
    <MatterContactConnectionRichCard
      contact={previewContact}
      matterContactConnection={matterContactConnection}
      {...(matterId
        ? { communicationActions: "call-text" as const, matterId }
        : { communicationActions: "call-email" as const })}
      tone={tone}
      hideConnectionDescription
      onClearSelection={onRemoveContact}
      className="w-full min-w-0 border-0 p-0 shadow-none"
    />
  );
}
